import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { CheckToken } from "../login/AccessToken";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom";
import "./BillingPayment.scss";
import { Avatar, Box, Card, CardContent, Divider, IconButton, Tooltip } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Autocomplete from "@mui/material/Autocomplete";
import SideNavbar from "./SideNavbar";
import { toast } from "react-toastify";
import {
  Grid,
  TextField,
  Button,
  Typography,
} from "@mui/material";
import { ReactComponent as CalendarIcon } from './DummyIcons/calendar-silhouette 2.svg';
import AddIcon from "@mui/icons-material/Add";
import { displayPatientName } from "../../utils/displayPatientName";
import { normalizeBase64Image } from "../utils/utils";
import InvoicePatientSearchDialog from "./InvoicePatientSearchDialog";

// Local calendar date as YYYY-MM-DD (toISOString is UTC and gives yesterday before 05:30 IST)
const getLocalDateString = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Same screen as EstimateAdditon.js ("Add New Estimate"), prefilled from the estimate being edited.
const EditEstimate = () => {
  const [itemsList, setItemsList] = useState([
    { items: "", quantity: "", price: "", amount: "", discount: "", category: "services" },
  ]);
  const [discount, setDiscount] = useState(0);
  // Bill discount can be entered as % or Rs; the other is derived (UI only — API still gets %)
  const [billDiscountPctInput, setBillDiscountPctInput] = useState("");
  const [billDiscountAmtInput, setBillDiscountAmtInput] = useState("");
  const [billDiscountMode, setBillDiscountMode] = useState("pct"); // which field the user last typed in
  const [tax, setTax] = useState(0);
  const [notes, setNotes] = useState("");
  const [patientId, setPatientId] = useState("");
  // Walk-in patient: typed name instead of a registered patient (patientId stays empty)
  const [patientMode, setPatientMode] = useState("search"); // "search" | "manual"
  const [walkInPatientName, setWalkInPatientName] = useState("");
  const [estimateNumber, setEstimateNumber] = useState("");
  const [estimateDate, setEstimateDate] = useState(getLocalDateString);

  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const [patientList, setPatientList] = useState([]);
  const [isServicesLoading, setIsServicesLoading] = useState(false);
  const [serviceSearchInputByRow, setServiceSearchInputByRow] = useState({}); // index -> search string for services Autocomplete
  const searchDebounceRef = useRef(null);
  const selectedServicesCacheRef = useRef(new Map()); // id -> option, so selected services stay visible when not in search results
  const [openPatientSearch, setOpenPatientSearch] = useState(false);
  const [servicesList, setServicesList] = useState([]);

  const [authToken, setAuthToken] = useState(
    sessionStorage.getItem("accessToken") !== undefined &&
      sessionStorage.getItem("accessToken") !== null
      ? sessionStorage.getItem("accessToken")
      : ""
  );
  const today = getLocalDateString();
  const history = useHistory();
  const location = useLocation();
  const headers = {
    Authorization: `Bearer ${authToken}`,
  };
  const [facilityName, setFacilityName] = useState(
    sessionStorage.getItem("facility_name") || ""
  );

  useEffect(() => {
    if (
      sessionStorage.getItem("accessToken") == undefined &&
      sessionStorage.getItem("accessToken") == null
    ) {
      history.push("/login");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Token = () => {
    const check = CheckToken();
    console.log(check);
    setAuthToken(sessionStorage.getItem("accessToken"));
    return check;
  };

  // Prefill the form from the estimate being edited
  useEffect(() => {
    const { data } = location.state || {};
    if (!data) return;

    if (data.patient_id) {
      setPatientMode("search");
      setPatientId(data.patient_id);
    } else {
      // Walk-in estimate — no registered patient, only a typed name
      setPatientMode("manual");
      setWalkInPatientName(data.name || data.patient_name || "");
    }
    setEstimateNumber(data.estimate_number || "");
    setEstimateDate(data.estimate_date || getLocalDateString());
    setNotes(data.note || "");
    setTax(data.tax || 0);

    const initialDiscount = data.discount || 0;
    setDiscount(initialDiscount);
    setBillDiscountMode("pct");
    setBillDiscountPctInput(initialDiscount ? String(initialDiscount) : "");

    const initialItemsList = (data.items || []).map((item) => {
      // Seed the search cache so the Autocomplete can show this service without a live search
      selectedServicesCacheRef.current.set(item.item_id, {
        medical_service_id: item.item_id,
        name: item.item_name,
        fee: item.item_price,
      });
      return {
        items: item.item_id,
        quantity: String(item.quantity),
        price: Number(item.item_price || 0).toFixed(2),
        amount: Number(item.subtotal || 0).toFixed(2),
        discount: item.discount_percentage ? String(item.discount_percentage) : "",
        category: "services",
      };
    });
    if (initialItemsList.length > 0) setItemsList(initialItemsList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Services are searched on demand (OPD episode), same as the Add Estimate screen
  const searchMedicalServices = (query) => {
    if (!authToken) return;
    if (!query || query.trim() === "") {
      setServicesList([]);
      setIsServicesLoading(false);
      return;
    }
    setIsServicesLoading(true);
    axios
      .post(
        `${REACT_APP_BACKEND_ROUTE_URL}get_medical_services/?episode_type=OPD`,
        { query: query },
        { headers }
      )
      .then((res) => {
        const list = res.data?.data ?? res.data?.service_list ?? [];
        setServicesList(
          list.filter((service) => !service.expired && service.active)
        );
      })
      .catch((err) => console.error("Error searching medical services:", err))
      .finally(() => setIsServicesLoading(false));
  };

  const DEBOUNCE_MS = 1500;
  const handleServiceSearchInputChange = (rowIndex, value) => {
    setServiceSearchInputByRow((prev) => ({ ...prev, [rowIndex]: value }));
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      searchMedicalServices(value);
      searchDebounceRef.current = null;
    }, DEBOUNCE_MS);
  };

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (authToken !== "") {
      axios
        .get(
          `${REACT_APP_BACKEND_ROUTE_URL}get_patient_list_with_aabha_info/`,
          {
            headers: headers,
          }
        )
        .then((res) => {
          if (res.data) {
            setPatientList(res.data?.data || []);
          } else {
            console.log("Error");
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const handleAddItem = () => {
    setItemsList([
      ...itemsList,
      { items: "", quantity: "", price: "", amount: "", discount: "", category: "services" },
    ]);
  };

  // qty × price less the line-item discount (whole-number %, 0–100, as stored by the backend)
  const calculateLineItemAmount = (quantity, price, discountPercent) => {
    const baseAmount = (parseFloat(quantity) || 0) * (parseFloat(price) || 0);
    const discountValue = Math.min(Math.max(parseInt(discountPercent, 10) || 0, 0), 100);
    return (baseAmount - (baseAmount * discountValue) / 100).toFixed(2);
  };

  const handleItemChange = (index, field, value) => {
    const updatedItemsList = [...itemsList];
    updatedItemsList[index][field] = value;

    if (field === "items") {
      const selectedItem = [
        ...selectedServicesCacheRef.current.values(),
        ...servicesList,
      ].find((item) => item.medical_service_id === value);
      if (selectedItem) {
        // Default quantity to 1 so the row doesn't need a separate manual entry
        if (!updatedItemsList[index].quantity) {
          updatedItemsList[index].quantity = "1";
        }
        updatedItemsList[index].price = selectedItem.fee.toFixed
          ? selectedItem.fee.toFixed(2)
          : Number(selectedItem.fee).toFixed(2);
        updatedItemsList[index].amount = calculateLineItemAmount(
          updatedItemsList[index].quantity,
          selectedItem.fee,
          updatedItemsList[index].discount
        );
      } else {
        updatedItemsList[index].price = "";
        updatedItemsList[index].amount = "";
      }
    } else if (field === "quantity") {
      updatedItemsList[index].amount = calculateLineItemAmount(
        value,
        updatedItemsList[index].price,
        updatedItemsList[index].discount
      );
    } else if (field === "discount") {
      if (value !== "") {
        const numericDiscount = parseInt(value, 10);
        updatedItemsList[index].discount = String(
          Math.min(Math.max(isNaN(numericDiscount) ? 0 : numericDiscount, 0), 100)
        );
      }
      updatedItemsList[index].amount = calculateLineItemAmount(
        updatedItemsList[index].quantity,
        updatedItemsList[index].price,
        updatedItemsList[index].discount
      );
    }

    setItemsList(updatedItemsList);
  };

  // Keep the bill discount % (what the API gets) and its Rs equivalent in sync.
  // The field the user typed in stays fixed; the other follows, including when items change.
  useEffect(() => {
    const netItemsTotal = itemsList.reduce(
      (total, item) => total + parseFloat(item.amount || 0),
      0
    );
    if (billDiscountMode === "amt") {
      const amount = Math.min(parseFloat(billDiscountAmtInput) || 0, netItemsTotal);
      const percent =
        netItemsTotal > 0 ? Math.round((amount / netItemsTotal) * 100 * 1000) / 1000 : 0;
      setDiscount(percent);
      setBillDiscountPctInput(billDiscountAmtInput === "" ? "" : String(percent));
    } else {
      const percent = parseFloat(billDiscountPctInput) || 0;
      setDiscount(percent);
      setBillDiscountAmtInput(
        billDiscountPctInput === "" ? "" : ((netItemsTotal * percent) / 100).toFixed(2)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsList, billDiscountMode, billDiscountPctInput, billDiscountAmtInput]);

  const handleBillDiscountPctChange = (value) => {
    setBillDiscountMode("pct");
    if (value !== "" && parseFloat(value) > 100) value = "100";
    setBillDiscountPctInput(value);
  };

  const handleBillDiscountAmtChange = (value) => {
    setBillDiscountMode("amt");
    const netItemsTotal = itemsList.reduce(
      (total, item) => total + parseFloat(item.amount || 0),
      0
    );
    if (value !== "" && parseFloat(value) > netItemsTotal) value = netItemsTotal.toFixed(2);
    setBillDiscountAmtInput(value);
  };

  const handleTotalChange = (field, value) => {
    if (field === "tax") {
      setTax(parseFloat(value) || 0);
    }
  };

  const calculateTotalAmount = () => {
    const itemsTotal = itemsList.reduce(
      (total, item) => total + parseFloat(item.amount || 0),
      0
    );
    const discountAmount = itemsTotal * (discount / 100);
    const taxAmount = (itemsTotal - discountAmount) * (tax / 100);
    const totalAmount = itemsTotal + taxAmount - discountAmount;
    return totalAmount.toFixed(2);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const filteredItems = itemsList.filter(
      (item) =>
        item.items &&
        item.quantity != null &&
        item.quantity !== ""
    );

    if (filteredItems.length !== itemsList.length) {
      toast.error("Please select an item and enter the quantity for all rows.");
      return;
    }

    const total = calculateTotalAmount();
    if (isNaN(total) || total <= 0) {
      toast.error("Total amount must be a valid number greater than 0.");
      return;
    }

    let walkInName;
    if (patientMode === "manual") {
      walkInName = walkInPatientName.trim();
      if (!walkInName) {
        toast.error("Please enter the patient's name.");
        return;
      }
    } else if (!patientId) {
      toast.error("Please select a patient.");
      return;
    }

    // Expiry date field was removed from the form; backend still expects it
    const expiryDate = estimateDate;

    const items = itemsList.map((item) => ({
      item_id: item.items,
      quantity: parseInt(item.quantity, 10) || 0,
      discount_percentage: parseInt(item.discount, 10) || 0,
    }));

    const estimatePayload = {
      hip_id: facilityName,
      estimate_number: estimateNumber,
      total_amount: total,
      estimate_date: estimateDate,
      expiry_date: expiryDate,
      // Registered patient vs. walk-in (typed name) — backend accepts either
      ...(patientMode === "manual"
        ? { patient_name: walkInName }
        : { patient_id: patientId }),
      items: items,
      note: notes,
      discount: discount,
      tax: tax,
    };

    axios
      .post(
        `${REACT_APP_BACKEND_ROUTE_URL}update_estimate/`,
        JSON.stringify(estimatePayload),
        {
          headers: headers,
        }
      )
      .then((response) => {
        if (response.data.status === "Success") {
          toast.success(response.data.message);
          setTimeout(() => {
            history.push("/doctor/estimates");
          }, 2000);
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        console.error("Error submitting estimate:", error);
      });
  };

  const handleRemoveItem = (indexToRemove) => {
    const updatedItemsList = itemsList.filter(
      (item, index) => index !== indexToRemove
    );
    setItemsList(updatedItemsList);
    // Search text is keyed by row index — shift it along with the rows
    setServiceSearchInputByRow((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        const i = Number(key);
        if (i < indexToRemove) next[i] = prev[key];
        else if (i > indexToRemove) next[i - 1] = prev[key];
      });
      return next;
    });
  };

  const handleNotesChange = (value) => {
    setNotes(value);
  };

  const handleGoBack = () => {
    history.push("/doctor/estimates");
  };

  // Patients found via search may not be in the preloaded list; merge them in
  const handleSelectPatient = (patient) => {
    setPatientList((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      if (list.some((p) => p.patient_id === patient.patient_id)) return list;
      return [...list, patient];
    });
    setPatientId(patient.patient_id);
  };

  const itemsTotal = itemsList.reduce(
    (total, item) => total + parseFloat(item.amount || 0),
    0
  );
  const grossItemsTotal = itemsList.reduce(
    (total, item) =>
      total + (parseFloat(item.quantity) || 0) * (parseFloat(item.price) || 0),
    0
  );
  const lineDiscountTotal = grossItemsTotal - itemsTotal;
  const labelStyle = {
    fontSize: "13px",
    fontWeight: 600,
    color: "#5f6b76",
    marginBottom: "6px",
    display: "block",
  };
  const fieldStyle = { borderRadius: "8px", border: "1px solid #ced4da", height: "42px" };
  const readOnlyStyle = { ...fieldStyle, backgroundColor: "#f5f7f9" };
  const cardSx = {
    borderRadius: "10px",
    padding: "20px 24px !important",
    marginBottom: "12px !important",
    boxShadow: "0px 2px 4px rgba(0,0,0,0.05)",
  };
  const blockSignKeys = (e) => {
    if (e.key === "-" || e.key === "+") {
      e.preventDefault();
    }
  };

  return (
    <>
      <SideNavbar >
        <div className="invoice-form-wrapper">
          <form onSubmit={handleSubmit}>
            {/* Patient & date */}
            <Card className="invoice-form-card" sx={cardSx}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1 }}>
                <p className="invoice-form-title" style={{ marginBottom: "4px" }}>
                  Edit Estimate
                </p>
                {estimateNumber && (
                  <Typography sx={{ color: "#5f6b76", fontSize: "14px", fontWeight: 600, mt: 1 }}>
                    Estimate No: {estimateNumber}
                  </Typography>
                )}
              </Box>
              <Typography sx={{ color: "#808285", fontSize: "14px", mb: 3 }}>
                Update this patient's cost estimate.
              </Typography>
              <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs={12} md={8}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <label className="required-label" style={labelStyle}>
                      Patient
                    </label>
                    <Button
                      type="button"
                      variant="text"
                      size="small"
                      onClick={() => {
                        if (patientMode === "manual") {
                          setPatientMode("search");
                          setWalkInPatientName("");
                        } else {
                          setPatientMode("manual");
                          setPatientId("");
                        }
                      }}
                      sx={{ textTransform: "none", fontSize: "12px", p: 0, minWidth: 0, mb: "6px" }}
                    >
                      {patientMode === "manual"
                        ? "Search registered patient instead"
                        : "Or type patient name (walk-in)"}
                    </Button>
                  </Box>
                  {patientMode === "manual" ? (
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Enter patient's full name"
                      value={walkInPatientName}
                      onChange={(e) => setWalkInPatientName(e.target.value)}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          height: "42px",
                          backgroundColor: "#fff",
                        },
                      }}
                    />
                  ) : (() => {
                    const selectedPatient =
                      patientList.find((p) => p.patient_id === patientId) ||
                      null;

                    if (!selectedPatient) {
                      return (
                        <Card
                          onClick={() => setOpenPatientSearch(true)}
                          sx={{
                            borderRadius: "10px",
                            border: "1px dashed #125386",
                            boxShadow: "none",
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "action.hover" },
                          }}
                        >
                          <CardContent sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                            <Avatar sx={{ bgcolor: "#213360", width: 56, height: 56 }}>
                              P
                            </Avatar>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <Typography sx={{ fontWeight: 700 }}>
                                No patient selected
                              </Typography>
                              <Typography variant="body2" sx={{ color: "#6B7A90" }}>
                                Search by Patient ID / ABHA / Phone / Demographics
                              </Typography>
                            </div>
                            <Button
                              type="button"
                              variant="outlined"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenPatientSearch(true);
                              }}
                              sx={{ textTransform: "none", color: "#125386", borderColor: "#125386", whiteSpace: "nowrap" }}
                            >
                              Search patient
                            </Button>
                          </CardContent>
                        </Card>
                      );
                    }

                    const photoSrc = normalizeBase64Image(selectedPatient.photo);
                    const age =
                      selectedPatient.age !== undefined && selectedPatient.age !== null
                        ? `${selectedPatient.age}`
                        : "-";
                    const phone =
                      selectedPatient.phone_no || selectedPatient.phone_number || "-";

                    return (
                      <>
                        <Card
                          onClick={() => setOpenPatientSearch(true)}
                          sx={{
                            borderRadius: "10px",
                            border: "1px solid #125386",
                            boxShadow: "none",
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "action.hover" },
                          }}
                        >
                          <CardContent sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                            <Avatar
                              src={photoSrc || undefined}
                              alt={displayPatientName(selectedPatient.name, "Patient")}
                              sx={{ width: 56, height: 56, bgcolor: "#213360" }}
                            >
                              {String(selectedPatient?.name || "P").slice(0, 1).toUpperCase()}
                            </Avatar>
                            <div style={{ minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 800, textTransform: "uppercase" }}>
                                {displayPatientName(selectedPatient.name, "-")}
                              </Typography>
                              <Typography variant="body2">
                                Patient ID: {selectedPatient.patient_id || "-"}
                              </Typography>
                              <Typography variant="body2" className="invoice-patient-meta" component="div">
                                <span>Gender: {selectedPatient.gender || "-"}</span>
                                <span>DOB: {selectedPatient.dob || "-"}</span>
                                <span>Age: {age}</span>
                              </Typography>
                              <Typography variant="body2">Phone: {phone}</Typography>
                            </div>
                          </CardContent>
                        </Card>
                        <div style={{ display: "flex", gap: 8 }}>
                          <Button
                            type="button"
                            variant="text"
                            size="small"
                            onClick={() => setOpenPatientSearch(true)}
                            style={{ textTransform: "none", paddingLeft: 0 }}
                          >
                            Change patient
                          </Button>
                          <Button
                            type="button"
                            variant="text"
                            size="small"
                            onClick={() => setPatientId("")}
                            style={{ textTransform: "none" }}
                          >
                            Clear
                          </Button>
                        </div>
                      </>
                    );
                  })()}
                </Grid>

                <Grid item xs={12} md={4}>
                  <label htmlFor="estimateDate" className="required-label" style={labelStyle}>
                    Estimate Date
                  </label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid #ced4da",
                      borderRadius: "8px",
                      height: "42px",
                      backgroundColor: "#fff",
                      paddingRight: "12px",
                    }}
                  >
                    <input
                      type="date"
                      className="form-control"
                      id="estimateDate"
                      name="estimateDate"
                      required
                      min={today}
                      max="2050-01-01"
                      value={estimateDate}
                      onChange={(e) => setEstimateDate(e.target.value)}
                      style={{ border: "none", boxShadow: "none", height: "100%", flex: 1, minWidth: 0, background: "transparent" }}
                    />
                    <CalendarIcon
                      style={{ flex: "none", cursor: "pointer" }}
                      onClick={() =>
                        document.getElementById("estimateDate").showPicker?.()
                      }
                    />
                  </div>
                </Grid>
              </Grid>
            </Card>

            {/* Line items */}
            <Card className="invoice-form-card" sx={cardSx}>
              <Typography sx={{ fontWeight: 700, fontSize: "18px", color: "#172935", mb: 2 }}>
                Items
              </Typography>

              {/* Column headers (desktop only) */}
              <Box sx={{ display: { xs: "none", md: "block" }, px: 2, mb: 1 }}>
                <Grid container spacing={2}>
                  <Grid item md={5.5}><span style={labelStyle}>Service</span></Grid>
                  <Grid item md={1.5}><span style={labelStyle}>Quantity</span></Grid>
                  <Grid item md={1.5}><span style={labelStyle}>Price (Rs)</span></Grid>
                  <Grid item md={1}><span style={labelStyle}>Disc (%)</span></Grid>
                  <Grid item md={1.5}><span style={labelStyle}>Amount (Rs)</span></Grid>
                </Grid>
              </Box>

              {itemsList.map((item, index) => (
                <Box key={index} className="invoice-service-row" sx={{ border: "1px solid #d6e9f8" }}>
                  <Grid container spacing={2} alignItems="center">
                    {/* Service Selection */}
                    <Grid item xs={12} md={5.5}>
                      {(() => {
                        const selectedOption =
                          servicesList.find((s) => s.medical_service_id === item.items) ||
                          selectedServicesCacheRef.current.get(item.items) ||
                          null;
                        const options =
                          selectedOption &&
                          !servicesList.some(
                            (s) => s.medical_service_id === selectedOption.medical_service_id
                          )
                            ? [selectedOption, ...servicesList]
                            : servicesList;

                        return (
                          <Autocomplete
                            size="small"
                            fullWidth
                            options={options}
                            value={selectedOption}
                            loading={isServicesLoading}
                            getOptionLabel={(option) => option?.name || ""}
                            isOptionEqualToValue={(option, value) =>
                              option?.medical_service_id === value?.medical_service_id
                            }
                            noOptionsText={
                              isServicesLoading
                                ? "Loading..."
                                : serviceSearchInputByRow[index]
                                  ? "No services found"
                                  : "Type to search services"
                            }
                            onChange={(_, newValue) => {
                              if (!newValue) {
                                handleItemChange(index, "items", "");
                                setServiceSearchInputByRow((prev) => ({ ...prev, [index]: "" }));
                                return;
                              }
                              selectedServicesCacheRef.current.set(
                                newValue.medical_service_id,
                                newValue
                              );
                              setServiceSearchInputByRow((prev) => ({
                                ...prev,
                                [index]: newValue.name || "",
                              }));
                              handleItemChange(index, "items", newValue.medical_service_id);
                            }}
                            onInputChange={(event, newInputValue, reason) => {
                              if (reason === "input" || reason === "clear") {
                                handleServiceSearchInputChange(index, newInputValue);
                              }
                            }}
                            inputValue={
                              serviceSearchInputByRow[index] ?? selectedOption?.name ?? ""
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Search service"
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    backgroundColor: "#fff",
                                    minHeight: "42px",
                                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#ced4da" },
                                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#125386" },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#125386",
                                      borderWidth: "1.5px",
                                    },
                                  },
                                }}
                              />
                            )}
                          />
                        );
                      })()}
                    </Grid>

                    {/* Quantity Field */}
                    <Grid item xs={6} sm={3} md={1.5}>
                      <input
                        type="number"
                        className="form-control"
                        style={fieldStyle}
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) =>
                          handleItemChange(index, "quantity", e.target.value)
                        }
                        min="0"
                        onWheel={(e) => e.target.blur()}
                        onKeyDown={blockSignKeys}
                      />
                    </Grid>

                    {/* Price Field */}
                    <Grid item xs={6} sm={3} md={1.5}>
                      <input
                        type="text"
                        className="form-control"
                        style={readOnlyStyle}
                        value={item.price}
                        readOnly
                        placeholder="Price"
                      />
                    </Grid>

                    {/* Line Item Discount Field */}
                    <Grid item xs={6} sm={3} md={1}>
                      <input
                        type="number"
                        className="form-control"
                        style={fieldStyle}
                        placeholder="0"
                        value={item.discount || ""}
                        onChange={(e) =>
                          handleItemChange(index, "discount", e.target.value)
                        }
                        min="0"
                        max="100"
                        step="1"
                        onWheel={(e) => e.target.blur()}
                        onKeyDown={(e) => {
                          if (["-", "+", "e", "E", "."].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </Grid>

                    {/* Amount Field */}
                    <Grid item xs={6} sm={3} md={1.5}>
                      <input
                        type="text"
                        className="form-control"
                        style={{ ...readOnlyStyle, fontWeight: 600 }}
                        value={item.amount}
                        readOnly
                        placeholder="Amount"
                      />
                    </Grid>

                    {/* Action Field */}
                    <Grid
                      item
                      xs={12}
                      md={1}
                      sx={{ display: "flex", gap: 1, justifyContent: { xs: "flex-end", md: "center" } }}
                    >
                      {index === itemsList.length - 1 && (
                        <Tooltip title="Add item">
                          <IconButton
                            onClick={handleAddItem}
                            sx={{
                              color: "#fff",
                              backgroundColor: "#125386",
                              "&:hover": { backgroundColor: "#0e4169" },
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Remove item">
                        <IconButton
                          // Keep at least one row — the add (+) button lives on the last row
                          disabled={itemsList.length === 1}
                          onClick={() => handleRemoveItem(index)}
                          sx={{
                            color: "#d32f2f",
                            backgroundColor: "#fff",
                            border: "1px solid #f3c6c6",
                            "&:hover": { backgroundColor: "#fdecec" },
                            "&.Mui-disabled": { color: "#c4c4c4", borderColor: "#e0e0e0" },
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Card>

            {/* Notes & totals */}
            <Card className="invoice-form-card" sx={cardSx}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={7}>
                  <label htmlFor="notes" style={labelStyle}>Notes</label>
                  <textarea
                    id="notes"
                    name="notes"
                    placeholder="Add any notes for this estimate (optional)"
                    value={notes}
                    onChange={(e) => handleNotesChange(e.target.value)}
                    style={{
                      width: "100%",
                      height: "210px",
                      borderRadius: "8px",
                      border: "1px solid #ced4da",
                      padding: "12px 16px",
                      resize: "vertical",
                    }}
                    className="responsive-textarea"
                  />
                </Grid>

                <Grid item xs={12} md={5}>
                  <Box sx={{ backgroundColor: "#f7fafc", border: "1px solid #e3e8ee", borderRadius: "10px", p: 2.5 }}>
                    {lineDiscountTotal > 0.004 && (
                      <>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                          <span style={{ color: "#5f6b76" }}>Items total</span>
                          <span style={{ color: "#172935" }}>Rs {grossItemsTotal.toFixed(2)}</span>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                          <span style={{ color: "#5f6b76" }}>Item discounts</span>
                          <span style={{ color: "#2e7d32" }}>- Rs {lineDiscountTotal.toFixed(2)}</span>
                        </Box>
                      </>
                    )}
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                      <span style={{ color: "#5f6b76", fontWeight: 600 }}>Subtotal</span>
                      <span style={{ color: "#172935", fontWeight: 600 }}>Rs {itemsTotal.toFixed(2)}</span>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <label htmlFor="discount" style={labelStyle}>Bill Discount (%)</label>
                        <input
                          type="number"
                          className="form-control"
                          id="discount"
                          name="discount"
                          placeholder="0"
                          value={billDiscountPctInput}
                          onChange={(e) => handleBillDiscountPctChange(e.target.value)}
                          min="0"
                          max="100"
                          step="any"
                          onWheel={(e) => e.target.blur()}
                          onKeyDown={blockSignKeys}
                          style={fieldStyle}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <label htmlFor="discountAmount" style={labelStyle}>Bill Discount (Rs)</label>
                        <input
                          type="number"
                          className="form-control"
                          id="discountAmount"
                          name="discountAmount"
                          placeholder="0.00"
                          value={billDiscountAmtInput}
                          onChange={(e) => handleBillDiscountAmtChange(e.target.value)}
                          min="0"
                          step="any"
                          onWheel={(e) => e.target.blur()}
                          onKeyDown={blockSignKeys}
                          style={fieldStyle}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <label htmlFor="tax" style={labelStyle}>Tax (%)</label>
                        <input
                          type="number"
                          className="form-control"
                          id="tax"
                          name="tax"
                          value={tax}
                          onChange={(e) =>
                            handleTotalChange("tax", e.target.value)
                          }
                          min="0"
                          step="any"
                          onWheel={(e) => e.target.blur()}
                          onKeyDown={blockSignKeys}
                          style={fieldStyle}
                        />
                      </Grid>
                    </Grid>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <label
                        htmlFor="totalAmount"
                        style={{ color: "#172935", fontWeight: 700, fontSize: "16px", margin: 0 }}
                      >
                        Total Amount (Rs)
                      </label>
                      <input
                        type="text"
                        id="totalAmount"
                        name="totalAmount"
                        value={calculateTotalAmount()}
                        readOnly
                        style={{
                          border: "none",
                          background: "transparent",
                          textAlign: "right",
                          fontWeight: 700,
                          fontSize: "20px",
                          color: "#125386",
                          width: "50%",
                          outline: "none",
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 3,
                  flexDirection: { xs: "column-reverse", sm: "row" },
                }}
              >
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleGoBack}
                  sx={{ minWidth: 140, borderRadius: "8px", color: "#125386", borderColor: "#125386", fontWeight: 600 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    minWidth: 160,
                    borderRadius: "8px",
                    backgroundColor: "#125386",
                    fontWeight: 600,
                    "&:hover": { backgroundColor: "#0e4169" },
                  }}
                >
                  Save Changes
                </Button>
              </Box>
            </Card>
          </form>
          <InvoicePatientSearchDialog
            open={openPatientSearch}
            onClose={() => setOpenPatientSearch(false)}
            authToken={authToken}
            backendUrl={REACT_APP_BACKEND_ROUTE_URL}
            onSelectPatient={handleSelectPatient}
          />
        </div>
      </SideNavbar>
    </>
  );
};

export default EditEstimate;
